import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Formik } from 'formik';
import { useToasts } from 'react-toast-notifications';
import * as Yup from 'yup';
import { FiArrowRight, FiEye, FiEyeOff, FiLock, FiMail } from 'react-icons/fi';

import { useAuth } from '../../hooks/auth';

import logoSymbol from '../../assets/logo/1x/symbol.png';
import logoVertical from '../../assets/logo/1x/logoWhite.png';
import logoHorizontalWhite from '../../assets/logo/1x/logoHorizontalWhite.png';
import logoHorizontal from '../../assets/logo/1x/logoHorizontal.png';

import {
  BrandBlock,
  Check,
  Field,
  FormBlock,
  FormWrap,
  ForgotLink,
  Helper,
  HeroContent,
  HeroStats,
  InputWrapper,
  LeftPanel,
  PageContainer,
  RightPanel,
  RowBetween,
  Splash,
  SplashLogoBox,
  SplashTagline,
  SubmitButton,
} from './styles';

interface ISignIn {
  email: string;
  password: string;
}

const REMEMBER_EMAIL_KEY = '@VivaVoz:rememberedEmail';

const validationSchema = Yup.object().shape({
  email: Yup.string().email('E-mail inválido').required('E-mail é obrigatório'),
  password: Yup.string().required('Senha é obrigatória'),
});

const SPLASH_VARIANTS = [
  { key: 'symbol', src: logoSymbol, className: 'variant-symbol' },
  { key: 'vertical', src: logoVertical, className: 'variant-vertical' },
  {
    key: 'horizontal',
    src: logoHorizontalWhite,
    className: 'variant-horizontal',
  },
] as const;

const SignIn: React.FC = () => {
  const { addToast } = useToasts();
  const { signIn } = useAuth();
  const formRef = useRef<HTMLFormElement | null>(null);

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [splashStage, setSplashStage] = useState(0);
  const [splashDone, setSplashDone] = useState(false);

  const [rememberedEmail] = useState<string>(() => {
    try {
      return localStorage.getItem(REMEMBER_EMAIL_KEY) || '';
    } catch {
      return '';
    }
  });
  const [remember, setRemember] = useState<boolean>(() => !!rememberedEmail);

  useEffect(() => {
    document.title = 'Login - Viva Voz - Rede Suco de Laranja';
  }, []);

  useEffect(() => {
    // Cicla as variantes da logo na splash screen e depois dissolve.
    const timers: number[] = [];
    timers.push(window.setTimeout(() => setSplashStage(1), 650));
    timers.push(window.setTimeout(() => setSplashStage(2), 1250));
    timers.push(window.setTimeout(() => setSplashDone(true), 2100));
    return () => timers.forEach(id => window.clearTimeout(id));
  }, []);

  useEffect(() => {
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = 'auto';
      document.body.style.overflow = 'auto';
    };
  }, []);

  const handleSignIn = useCallback(
    async (data: ISignIn) => {
      setLoading(true);
      try {
        await signIn(data);
        try {
          if (remember) {
            localStorage.setItem(REMEMBER_EMAIL_KEY, data.email);
          } else {
            localStorage.removeItem(REMEMBER_EMAIL_KEY);
          }
        } catch {
          /* storage indisponível — ignora silenciosamente */
        }
        addToast('Login bem-sucedido!', {
          appearance: 'success',
          autoDismiss: true,
          autoDismissTimeout: 3000,
        });
      } catch (error) {
        addToast('Erro no login. Verifique suas credenciais.', {
          appearance: 'error',
          autoDismiss: true,
          autoDismissTimeout: 3000,
        });
      } finally {
        setLoading(false);
      }
    },
    [signIn, addToast, remember],
  );

  return (
    <>
      <Splash $done={splashDone} aria-hidden={splashDone}>
        <SplashLogoBox>
          {SPLASH_VARIANTS.map((variant, idx) => (
            <img
              key={variant.key}
              src={variant.src}
              alt="Viva Voz"
              className={`${variant.className}${
                idx === splashStage ? ' active' : ''
              }`}
            />
          ))}
        </SplashLogoBox>
        <SplashTagline $visible={splashStage >= 2}>
          Rede Suco de Laranja
        </SplashTagline>
      </Splash>

      <PageContainer>
        <LeftPanel>
          <FormWrap>
            <BrandBlock $delay={2200}>
              <img src={logoHorizontal} alt="Viva Voz" />
              <h1>Bem-vindo de volta</h1>
              <p>Acesse sua conta para continuar o atendimento.</p>
            </BrandBlock>

            <Formik
              initialValues={{ email: rememberedEmail, password: '' }}
              onSubmit={values => handleSignIn(values)}
              validationSchema={validationSchema}
            >
              {({
                values,
                errors,
                touched,
                handleBlur,
                handleChange,
                handleSubmit,
              }) => (
                <FormBlock ref={formRef} onSubmit={handleSubmit} $delay={2350}>
                  <Field>
                    <label htmlFor="email">
                      <span className="field-label">E-mail</span>
                      <InputWrapper hasError={touched.email && !!errors.email}>
                        <FiMail className="input-icon" />
                        <input
                          id="email"
                          type="email"
                          placeholder="voce@empresa.com"
                          autoComplete="email"
                          onChange={handleChange('email')}
                          onBlur={handleBlur('email')}
                          value={values.email}
                        />
                      </InputWrapper>
                    </label>
                    {touched.email && errors.email && (
                      <span className="error">{errors.email}</span>
                    )}
                  </Field>

                  <Field>
                    <label htmlFor="password">
                      <span className="field-label">Senha</span>
                      <InputWrapper
                        hasError={touched.password && !!errors.password}
                      >
                        <FiLock className="input-icon" />
                        <input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Digite sua senha"
                          autoComplete="current-password"
                          onChange={handleChange('password')}
                          onBlur={handleBlur('password')}
                          value={values.password}
                        />
                        <button
                          type="button"
                          className="eye-toggle"
                          onClick={() => setShowPassword(p => !p)}
                          aria-label={
                            showPassword ? 'Ocultar senha' : 'Mostrar senha'
                          }
                        >
                          {showPassword ? (
                            <FiEyeOff size={16} />
                          ) : (
                            <FiEye size={16} />
                          )}
                        </button>
                      </InputWrapper>
                    </label>
                    {touched.password && errors.password && (
                      <span className="error">{errors.password}</span>
                    )}
                  </Field>

                  <RowBetween>
                    <Check>
                      <input
                        type="checkbox"
                        checked={remember}
                        onChange={e => setRemember(e.target.checked)}
                      />
                      Lembrar de mim
                    </Check>
                    <ForgotLink href="/forgot-password">
                      Esqueceu a senha?
                    </ForgotLink>
                  </RowBetween>

                  <SubmitButton type="submit" disabled={loading}>
                    {loading ? (
                      <span className="spinner" />
                    ) : (
                      <>
                        Entrar
                        <FiArrowRight size={16} />
                      </>
                    )}
                  </SubmitButton>
                </FormBlock>
              )}
            </Formik>

            <Helper $delay={2500}>
              Precisa de ajuda? Fale com o suporte do seu sindicato.
            </Helper>
          </FormWrap>
        </LeftPanel>

        <RightPanel $delay={2100}>
          <div className="pattern" />
          <HeroContent>
            <img src={logoHorizontalWhite} alt="Viva Voz" />
            <h2>Conectando trabalhadores e sindicatos</h2>
            <p>
              Denúncias, cadastros, pesquisas e pré-filiação em uma única
              plataforma. Gestão transparente e ágil para a Rede Suco de
              Laranja.
            </p>
          </HeroContent>

          <HeroStats>
            <div className="stat">
              <strong>+12k</strong>
              <span>Trabalhadores</span>
            </div>
            <div className="stat">
              <strong>+400</strong>
              <span>Denúncias</span>
            </div>
            <div className="stat">
              <strong>+20</strong>
              <span>Sindicatos</span>
            </div>
          </HeroStats>
        </RightPanel>
      </PageContainer>
    </>
  );
};

export default SignIn;
